import { useState, useEffect } from 'react'
import { api } from '@/services/api-client'
import { useAvailableModels } from '@/hooks/queries'
import { useUIStore } from '@/stores'
import {
  Search, Plus, Trash2, X, ChevronRight, Loader2, Sparkles, ArrowLeft, Globe,
  FileText, Calendar, Clock, CheckCircle, Send, BarChart3, Target,
  ShoppingCart
} from 'lucide-react'
import { getModelShortName } from '@/utils/helpers'
import { WysiwygEditor } from '@/components/ui/WysiwygEditor'

type Campaign = {
  id: string; site_url: string; site_analysis: any; article_count: number
  provider: string; model: string; blog_account_id: string
  schedule_frequency: string; schedule_time_mode: string; schedule_time_value: string
  status: string; created_at: string; articles?: SeoArticle[]
}

type SeoArticle = {
  id: string; campaign_id: string; title: string; keywords: string[]
  meta_description: string; content: string; word_count: number
  status: string; publish_date: string; sort_order: number
}

const ARTICLE_COUNTS = [10, 20, 50]

export function ContentSeoView() {
  const { data: availableModels } = useAvailableModels()
  const navigateToMarketplace = useUIStore(s => s.navigateToMarketplace)
  const hasProviders = availableModels && availableModels.length > 0

  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [activeCampaign, setActiveCampaign] = useState<Campaign | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generating, setGenerating] = useState<string | null>(null)
  const [blogAccounts, setBlogAccounts] = useState<any[]>([])
  const [openArticle, setOpenArticle] = useState<SeoArticle | null>(null)
  const [editingContent, setEditingContent] = useState<string | null>(null)

  const [form, setForm] = useState({
    site_url: '', article_count: 10, provider: '', model: '',
    blog_account_id: '', schedule_frequency: 'weekly',
    schedule_time_mode: 'preset', schedule_time_value: 'morning',
  })

  const selectedProviderModels = availableModels?.find(p => p.provider_type === form.provider)?.models || []

  useEffect(() => {
    if (availableModels && availableModels.length > 0 && !form.provider) {
      const first = availableModels[0]
      setForm(f => ({ ...f, provider: first.provider_type, model: first.models[0] || '' }))
    }
  }, [availableModels])

  const fetchCampaigns = async () => {
    setLoading(true)
    try { setCampaigns(await api.getSeoCampaigns()) } catch { /* */ } finally { setLoading(false) }
  }

  const fetchCampaign = async (id: string) => {
    setLoading(true)
    try {
      const c = await api.getSeoCampaign(id)
      c.articles = (c.articles || []).map((a: any) => ({
        ...a,
        keywords: typeof a.keywords === 'string' ? JSON.parse(a.keywords) : a.keywords,
      }))
      setActiveCampaign(c)
    } catch { /* */ } finally { setLoading(false) }
  }

  const fetchBlogAccounts = async () => {
    try {
      const accs = await api.getSocialAccounts()
      setBlogAccounts(accs.filter((a: any) => a.platform === 'blog_api'))
    } catch { /* */ }
  }

  useEffect(() => { fetchCampaigns(); fetchBlogAccounts() }, [])

  const pollJob = async (jobId: string, campaignId: string) => {
    const poll = setInterval(async () => {
      try {
        const job = await api.getJob(jobId)
        if (job.status === 'completed') {
          clearInterval(poll)
          setGenerating(null)
          await fetchCampaign(campaignId)
        } else if (job.status === 'failed') {
          clearInterval(poll)
          setGenerating(null)
          setError(job.error || 'Operacja nie powiodla sie')
        }
      } catch { /* */ }
    }, 3000)
  }

  const handleCreate = async () => {
    if (!form.site_url.trim() || !form.provider || !form.model) return
    setError(null)
    setGenerating('creating')
    try {
      const result = await api.createSeoCampaign(form)
      setShowCreate(false)
      setActiveCampaign(result)
      fetchCampaigns()
      if (result.job_id) pollJob(result.job_id, result.id)
    } catch (err: any) { setError(err?.message || 'Blad tworzenia kampanii'); setGenerating(null) }
  }

  const handleGenerateAll = async (campaignId: string) => {
    setGenerating('all')
    setError(null)
    try {
      const { job_ids } = await api.generateAllSeoArticles(campaignId)
      if (job_ids.length > 0) pollJob(job_ids[job_ids.length - 1], campaignId)
    } catch (err: any) { setError(err?.message || 'Blad generowania'); setGenerating(null) }
  }

  const handleGenerateOne = async (articleId: string, campaignId: string) => {
    setGenerating(articleId)
    setError(null)
    try {
      const { job_id } = await api.generateSeoArticle(articleId)
      pollJob(job_id, campaignId)
    } catch (err: any) { setError(err?.message || 'Blad generowania'); setGenerating(null) }
  }

  const handlePublishAll = async (campaignId: string) => {
    setGenerating('publishing')
    setError(null)
    try {
      await api.publishAllSeoArticles(campaignId)
      await fetchCampaign(campaignId)
      setGenerating(null)
      setError(null)
    } catch (err: any) { setError(err?.message || 'Blad publikacji'); setGenerating(null) }
  }

  const handleDelete = async (id: string) => {
    try {
      await api.deleteSeoCampaign(id)
      if (activeCampaign?.id === id) setActiveCampaign(null)
      fetchCampaigns()
    } catch { /* */ }
  }

  // ============ Article detail view ============
  if (openArticle) {
    return (
      <div className="seo-view">
        <div className="seo-article-modal">
          <button className="seo-back-btn" onClick={() => setOpenArticle(null)}>
            <ArrowLeft size={18} /> Wstecz
          </button>
          <h1 className="seo-article-title">{openArticle.title}</h1>
          <div className="seo-article-meta-bar">
            {openArticle.keywords.map(kw => <span key={kw} className="seo-keyword-tag">{kw}</span>)}
            {openArticle.word_count > 0 && <span className="seo-word-count">{openArticle.word_count} slow</span>}
          </div>
          {openArticle.meta_description && (
            <p className="seo-meta-desc"><strong>Meta:</strong> {openArticle.meta_description}</p>
          )}
          {openArticle.content ? (
            <>
              <div className="seo-article-actions-bar">
                {editingContent !== null ? (
                  <>
                    <button className="seo-btn seo-btn--generate" onClick={async () => {
                      try {
                        await api.updateAgentArticle(openArticle.id, { content: editingContent })
                        setOpenArticle({ ...openArticle, content: editingContent })
                        setEditingContent(null)
                      } catch { /* */ }
                    }}>
                      <CheckCircle size={14} /> Zapisz
                    </button>
                    <button className="seo-back-btn" onClick={() => setEditingContent(null)}><X size={14} /> Anuluj</button>
                  </>
                ) : (
                  <button className="seo-btn seo-btn--publish" onClick={() => setEditingContent(openArticle.content)}>
                    <FileText size={14} /> Edytuj tresc
                  </button>
                )}
              </div>
              {editingContent !== null ? (
                <WysiwygEditor value={editingContent} onChange={setEditingContent} />
              ) : (
                <div className="seo-article-content" dangerouslySetInnerHTML={{ __html: openArticle.content }} />
              )}
            </>
          ) : (
            <div className="seo-empty">
              <p>Artykul nie zostal jeszcze wygenerowany.</p>
              {activeCampaign && (
                <button className="seo-btn seo-btn--generate" onClick={() => { setOpenArticle(null); handleGenerateOne(openArticle.id, activeCampaign.id) }}>
                  <Sparkles size={14} /> Generuj tresc
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ============ Campaign detail ============
  if (activeCampaign) {
    const articles = activeCampaign.articles || []
    const analysis = activeCampaign.site_analysis || {}
    const generated = articles.filter(a => a.status === 'generated' || a.status === 'scheduled')
    const drafts = articles.filter(a => a.status === 'draft')

    return (
      <div className="seo-view">
        <div className="seo-detail">
          <button className="seo-back-btn" onClick={() => { setActiveCampaign(null); fetchCampaigns() }}>
            <ArrowLeft size={18} /> Kampanie
          </button>

          <div className="seo-detail-header">
            <div className="seo-detail-url">
              <Globe size={20} />
              <div>
                <h2>{activeCampaign.site_url}</h2>
                <span className={`seo-status seo-status--${activeCampaign.status}`}>
                  {activeCampaign.status === 'analyzing' ? 'Analiza...' : activeCampaign.status === 'ready' ? 'Gotowa' : activeCampaign.status === 'publishing' ? 'Publikowanie' : activeCampaign.status}
                </span>
              </div>
            </div>
          </div>

          {/* Analysis results */}
          {analysis.industry && (
            <div className="seo-analysis">
              <div className="seo-analysis-item">
                <BarChart3 size={14} /> <strong>Branza:</strong> {analysis.industry}
              </div>
              <div className="seo-analysis-item">
                <Target size={14} /> <strong>Grupa docelowa:</strong> {analysis.target_audience}
              </div>
              {analysis.primary_topics?.length > 0 && (
                <div className="seo-analysis-item">
                  <FileText size={14} /> <strong>Tematy:</strong> {analysis.primary_topics.join(', ')}
                </div>
              )}
              {analysis.seo_opportunities?.length > 0 && (
                <div className="seo-analysis-item seo-analysis-item--wide">
                  <Search size={14} /> <strong>Mozliwosci SEO:</strong>
                  <ul>{analysis.seo_opportunities.map((o: string, i: number) => <li key={i}>{o}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          {activeCampaign.status === 'analyzing' && (
            <div className="seo-loading">
              <Loader2 size={24} className="spin" />
              <p>AI analizuje strone i przygotowuje plan artykulow...</p>
            </div>
          )}

          {error && (
            <div className="seo-error">
              <X size={14} onClick={() => setError(null)} style={{ cursor: 'pointer' }} />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          {articles.length > 0 && (
            <div className="seo-actions-bar">
              {drafts.length > 0 && (
                <button className="seo-btn seo-btn--generate" onClick={() => handleGenerateAll(activeCampaign.id)} disabled={!!generating}>
                  {generating === 'all' ? <><Loader2 size={14} className="spin" /> Generowanie...</> : <><Sparkles size={14} /> Generuj wszystkie ({drafts.length})</>}
                </button>
              )}
              {generated.length > 0 && activeCampaign.blog_account_id && (
                <button className="seo-btn seo-btn--publish" onClick={() => handlePublishAll(activeCampaign.id)} disabled={!!generating}>
                  {generating === 'publishing' ? <><Loader2 size={14} className="spin" /> Planowanie...</> : <><Send size={14} /> Zaplanuj publikacje ({generated.length})</>}
                </button>
              )}
              <span className="seo-articles-count">
                {generated.length}/{articles.length} gotowych
              </span>
            </div>
          )}

          {/* Articles list */}
          {articles.length > 0 && (
            <div className="seo-articles-list">
              {articles.map(a => (
                <div key={a.id} className={`seo-article-card seo-article-card--${a.status}`}>
                  <div className="seo-article-card-left">
                    <div className="seo-article-card-info">
                      <button className="seo-article-link" onClick={() => setOpenArticle(a)}>
                        {a.title} <ChevronRight size={14} />
                      </button>
                      <div className="seo-article-card-meta">
                        <span className={`seo-status seo-status--${a.status}`}>
                          {a.status === 'draft' ? 'Szkic' : a.status === 'generated' ? 'Gotowy' : a.status === 'scheduled' ? 'Zaplanowany' : a.status}
                        </span>
                        {a.keywords.slice(0, 3).map(kw => <span key={kw} className="seo-keyword-sm">{kw}</span>)}
                        {a.word_count > 0 && <span>{a.word_count} slow</span>}
                      </div>
                    </div>
                  </div>
                  <div className="seo-article-card-actions">
                    {a.status === 'draft' && (
                      <button className="seo-btn-sm seo-btn--generate" onClick={() => handleGenerateOne(a.id, activeCampaign.id)} disabled={!!generating}>
                        {generating === a.id ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
                      </button>
                    )}
                    {a.status === 'generated' && <span className="seo-ready-badge"><CheckCircle size={13} /></span>}
                    {a.status === 'scheduled' && a.publish_date && (
                      <span className="seo-schedule-info"><Calendar size={12} /> {new Date(a.publish_date).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ============ Campaigns list ============
  return (
    <div className="seo-view">
      <div className="seo-header">
        <div className="seo-title-row">
          <Search size={24} className="seo-icon" />
          <div>
            <h1>ContentSEO-AI</h1>
            <p className="seo-desc">Automatyczne tworzenie artykulow SEO dla stron internetowych</p>
          </div>
        </div>
        <button className="seo-create-btn" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? <><X size={16} /> Anuluj</> : <><Plus size={16} /> Nowa kampania</>}
        </button>
      </div>

      {error && (
        <div className="seo-error">
          <X size={14} onClick={() => setError(null)} style={{ cursor: 'pointer' }} />
          <span>{error}</span>
        </div>
      )}

      {showCreate && (
        <div className="seo-create-form">
          <h3><Globe size={16} /> Nowa kampania SEO</h3>
          <div className="seo-form-grid">
            <label className="seo-field seo-field--wide">
              <span><Globe size={14} /> Adres strony</span>
              <input type="url" value={form.site_url} onChange={e => setForm(f => ({ ...f, site_url: e.target.value }))} placeholder="https://example.com" />
            </label>
            <label className="seo-field">
              <span>Liczba artykulow</span>
              <select value={form.article_count} onChange={e => setForm(f => ({ ...f, article_count: parseInt(e.target.value) }))}>
                {ARTICLE_COUNTS.map(n => <option key={n} value={n}>{n} artykulow</option>)}
              </select>
            </label>
            {hasProviders ? (
              <>
                <label className="seo-field">
                  <span>Provider AI</span>
                  <select value={form.provider} onChange={e => {
                    const prov = e.target.value
                    const models = availableModels?.find(p => p.provider_type === prov)?.models || []
                    setForm(f => ({ ...f, provider: prov, model: models[0] || '' }))
                  }}>
                    <option value="">-- Wybierz --</option>
                    {availableModels?.map(p => <option key={p.provider_type} value={p.provider_type}>{p.display_name}</option>)}
                  </select>
                </label>
                <label className="seo-field">
                  <span>Model</span>
                  <select value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} disabled={!form.provider}>
                    <option value="">-- Wybierz --</option>
                    {selectedProviderModels.map(m => <option key={m} value={m}>{getModelShortName(m)}</option>)}
                  </select>
                </label>
              </>
            ) : (
              <div className="seo-field seo-field--wide seo-no-provider">
                <ShoppingCart size={18} />
                <p>Brak klucza API. <button onClick={navigateToMarketplace}>Przejdz do Gieldy</button></p>
              </div>
            )}
            {blogAccounts.length > 0 && (
              <label className="seo-field">
                <span><Send size={14} /> Blog docelowy</span>
                <select value={form.blog_account_id} onChange={e => setForm(f => ({ ...f, blog_account_id: e.target.value }))}>
                  <option value="">-- Opcjonalnie --</option>
                  {blogAccounts.map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
                </select>
              </label>
            )}
            <label className="seo-field">
              <span><Calendar size={14} /> Czestotliwosc</span>
              <select value={form.schedule_frequency} onChange={e => setForm(f => ({ ...f, schedule_frequency: e.target.value }))}>
                <option value="daily">Codziennie</option>
                <option value="weekly">Co tydzien</option>
                <option value="biweekly">Co 2 tygodnie</option>
                <option value="monthly">Co miesiac</option>
              </select>
            </label>
            <label className="seo-field">
              <span><Clock size={14} /> Pora</span>
              <select value={form.schedule_time_value} onChange={e => setForm(f => ({ ...f, schedule_time_value: e.target.value }))}>
                <option value="morning">Rano (7-10)</option>
                <option value="noon">Poludnie (11-14)</option>
                <option value="evening">Wieczor (17-20)</option>
                <option value="night">Noc (21-24)</option>
              </select>
            </label>
          </div>
          <button className="seo-submit-btn" onClick={handleCreate} disabled={!form.site_url.trim() || !form.provider || !form.model || generating === 'creating'}>
            {generating === 'creating' ? <><Loader2 size={14} className="spin" /> Analizowanie strony...</> : <><Search size={14} /> Analizuj i zaplanuj</>}
          </button>
        </div>
      )}

      <div className="seo-campaigns-list">
        {loading && <p className="seo-loading-text"><Loader2 size={18} className="spin" /> Ladowanie...</p>}
        {!loading && campaigns.length === 0 && !showCreate && (
          <div className="seo-empty-state">
            <Search size={48} />
            <h3>Brak kampanii SEO</h3>
            <p>Podaj adres strony, a AI przeanalizuje ja i zaproponuje artykuly zoptymalizowane pod SEO.</p>
            <button className="seo-create-btn" onClick={() => setShowCreate(true)}>
              <Plus size={16} /> Nowa kampania
            </button>
          </div>
        )}
        {campaigns.map(c => (
          <div key={c.id} className="seo-campaign-card" onClick={() => fetchCampaign(c.id)}>
            <div className="seo-campaign-left">
              <Globe size={20} className="seo-campaign-icon" />
              <div>
                <h3>{c.site_url}</h3>
                <div className="seo-campaign-meta">
                  <span className={`seo-status seo-status--${c.status}`}>{c.status === 'analyzing' ? 'Analiza...' : c.status === 'ready' ? 'Gotowa' : c.status === 'publishing' ? 'Publikowanie' : c.status}</span>
                  <span>{c.article_count} artykulow</span>
                  <span>{getModelShortName(c.model)}</span>
                </div>
              </div>
            </div>
            <div className="seo-campaign-right">
              <span className="seo-campaign-date">{new Date(c.created_at).toLocaleDateString()}</span>
              <button className="seo-delete-btn" onClick={e => { e.stopPropagation(); handleDelete(c.id) }} title="Usun">
                <Trash2 size={14} />
              </button>
              <ChevronRight size={16} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
